import React from 'react'

import { Progress } from '@/ui'

import { SECTION_SHORT_LABELS } from '../lib/constants'
import type { ComplianceLessonKey } from '../lib/types'

interface ProgressSidebarProps {
  sections: ComplianceLessonKey[]
  currentIndex: number
  onJump: (index: number) => void
  /** Furthest section reachable by clicking — later segments render disabled
   *  rather than looking tappable and doing nothing. */
  maxReachableIndex: number
}

/**
 * Horizontal segmented progress bar (the DS `Progress` component, variant
 * "segmented" — same one Storybook's "SegmentedWithTitles" story documents).
 * Plain linear progress only — no per-section report status. Segments up to
 * the furthest reachable section are tappable to jump there directly.
 *
 * Below `sm` the segment titles are hidden and the bars carry the progress on
 * their own: five labels across a phone leaves ~53px each, which is narrower
 * than the longest of them. Hidden rather than dropped — `segmentTitles` still
 * feeds each segment's `aria-label` in the DS component, so the labels stay
 * available to assistive tech at every width. Done from here rather than in the
 * atom so the rest of the app's segmented progress bars are untouched; `Text`
 * always renders a `<p>`, which is what the selector reaches.
 *
 * The hover darkening is local for the same reason. `:not(:disabled)` confines
 * it to the reachable segments, which is what keeps the rest looking inert.
 */
export const ProgressSidebar: React.FC<ProgressSidebarProps> = ({
  sections,
  currentIndex,
  onJump,
  maxReachableIndex,
}) => (
  <div className="w-full max-sm:[&_p]:hidden [&_button:not(:disabled):hover>div>div]:bg-moss-300 [&_button:not(:disabled):hover>div]:bg-neutral-200">
    <Progress
      variant="segmented"
      numberOfSegments={sections.length}
      activeSegment={currentIndex + 1}
      segmentTitles={sections.map((s) => SECTION_SHORT_LABELS[s])}
      onSegmentClick={onJump}
      maxClickableSegment={maxReachableIndex}
      className="w-full"
    />
  </div>
)
